import api, { ApiResponse } from './api';

export interface Employee {
  id: number;
  employeeId: number;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string;
  departmentId: number;
  departmentName: string;
  positionId: number;
  positionName: string;
  positionTitle?: string;
  idNumber: string;
  hireDate: string;
  birthDate?: string;
  gender?: string;
  address?: string;
  isActive: boolean;
  leaveBalance?: number;
  photoUrl?: string;

  // Kisisel Bilgiler
  maritalStatus?: string;
  bloodType?: string;
  militaryStatus?: string;
  lastEducationLevel?: string;
  profession?: string;

  // Iletisim Bilgileri Extended
  homePhone?: string;
  mobilePhone2?: string;
  workPhone?: string;
  workExtension?: string;
  personalEmail?: string;
  city?: string;
  district?: string;
  postalCode?: string;

  // Acil Durum
  emergencyContact?: string;
  emergencyPhone?: string;

  // Aile Bilgileri
  spouseName?: string;
  fatherName?: string;
  motherName?: string;
  childCount?: number;

  // Finans & SGK
  socialSecurityNo?: string;
  salary?: number;
  bankName?: string;
  ibanNo?: string;

  // Izin Bilgileri
  annualLeaveDays?: number;

  // PDKS Device fields
  employeeCode?: string;
  cardNumber?: string;
  deviceUserId?: number;
  devicePassword?: string;
  hasFingerprint?: boolean;
  hasCard?: boolean;
  hasFace?: boolean;
  cardType?: string;
  privilege?: number;

  // eID / MRZ (BAC authentication)
  mrzDocumentNumber?: string;
  mrzDateOfBirth?: string;
  mrzDateOfExpiry?: string;
}

export interface EmployeeCreateDto {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  departmentId: number;
  positionId?: number;
  idNumber?: string;
  hireDate?: string;
  birthDate?: string;
  gender?: string;
  address?: string;

  // Kisisel Bilgiler
  maritalStatus?: string;
  bloodType?: string;
  militaryStatus?: string;
  lastEducationLevel?: string;
  profession?: string;

  // Iletisim Bilgileri Extended
  homePhone?: string;
  mobilePhone2?: string;
  workPhone?: string;
  workExtension?: string;
  personalEmail?: string;
  city?: string;
  district?: string;
  postalCode?: string;

  // Acil Durum
  emergencyContact?: string;
  emergencyPhone?: string;

  // Aile Bilgileri
  spouseName?: string;
  fatherName?: string;
  motherName?: string;
  childCount?: number;

  // Finans & SGK
  socialSecurityNo?: string;
  salary?: number;
  bankName?: string;
  ibanNo?: string;

  // Izin Bilgileri
  annualLeaveDays?: number;

  // PDKS Device fields
  employeeCode?: string;
  cardNumber?: string;
  deviceUserId?: number;
  devicePassword?: string;
  hasFingerprint?: boolean;
  hasCard?: boolean;
  hasFace?: boolean;
  cardType?: string;
  privilege?: number;

  // eID / MRZ (BAC authentication)
  mrzDocumentNumber?: string;
  mrzDateOfBirth?: string;
  mrzDateOfExpiry?: string;
}

export interface EmployeeUpdateDto {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  departmentId?: number;
  positionId?: number;
  idNumber?: string;
  hireDate?: string;
  birthDate?: string;
  gender?: string;
  address?: string;
  isActive?: boolean;

  // Kisisel Bilgiler
  maritalStatus?: string;
  bloodType?: string;
  militaryStatus?: string;
  lastEducationLevel?: string;
  profession?: string;

  // Iletisim Bilgileri Extended
  homePhone?: string;
  mobilePhone2?: string;
  workPhone?: string;
  workExtension?: string;
  personalEmail?: string;
  city?: string;
  district?: string;
  postalCode?: string;

  // Acil Durum
  emergencyContact?: string;
  emergencyPhone?: string;

  // Aile Bilgileri
  spouseName?: string;
  fatherName?: string;
  motherName?: string;
  childCount?: number;

  // Finans & SGK
  socialSecurityNo?: string;
  salary?: number;
  bankName?: string;
  ibanNo?: string;

  // Izin Bilgileri
  annualLeaveDays?: number;

  // PDKS Device fields
  employeeCode?: string;
  cardNumber?: string;
  deviceUserId?: number;
  devicePassword?: string;
  hasFingerprint?: boolean;
  hasCard?: boolean;
  hasFace?: boolean;
  cardType?: string;
  privilege?: number;

  // eID / MRZ (BAC authentication)
  mrzDocumentNumber?: string;
  mrzDateOfBirth?: string;
  mrzDateOfExpiry?: string;
}

export interface PagedResponse<T> {
  data: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface BulkDeleteResult {
  deletedIds: number[];
  failedIds: { id: number; reason: string }[];
}

const employeeService = {
  getAll: async (isActive?: boolean): Promise<ApiResponse<Employee[]>> => {
    const params = new URLSearchParams();
    params.append('pageSize', '10000'); // Get all employees
    if (isActive !== undefined) params.append('isActive', isActive.toString());
    return api.get(`/employees?${params.toString()}`);
  },

  getById: async (id: number): Promise<ApiResponse<Employee>> => {
    return api.get(`/employees/${id}`);
  },

  getPaged: async (
    page: number = 1,
    pageSize: number = 10,
    search?: string,
    departmentId?: number,
    isActive?: boolean
  ): Promise<ApiResponse<PagedResponse<Employee>>> => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('pageSize', pageSize.toString());
    if (search) params.append('search', search);
    if (departmentId) params.append('departmentId', departmentId.toString());
    if (isActive !== undefined) params.append('isActive', isActive.toString());

    return api.get(`/employees?${params.toString()}`);
  },

  create: async (data: EmployeeCreateDto): Promise<ApiResponse<{ id: number }>> => {
    return api.post('/employees', data);
  },

  update: async (id: number, data: EmployeeUpdateDto): Promise<ApiResponse<{ id: number }>> => {
    return api.put(`/employees/${id}`, data);
  },

  delete: async (id: number): Promise<ApiResponse<{ id: number }>> => {
    return api.delete(`/employees/${id}`);
  },

  bulkDelete: async (ids: number[]): Promise<ApiResponse<BulkDeleteResult>> => {
    return api.post('/employees/bulk-delete', { ids });
  },

  getByDepartment: async (departmentId: number): Promise<ApiResponse<Employee[]>> => {
    return api.get(`/employees?departmentId=${departmentId}&pageSize=500&isActive=true`);
  },

  getPhoto: async (id: number): Promise<ApiResponse<{ photoUrl: string | null }>> => {
    return api.get(`/employees/${id}/photo`);
  },

  uploadPhoto: async (id: number, photoBase64: string | null): Promise<ApiResponse<{ id: number }>> => {
    return api.post(`/employees/${id}/photo`, { photoBase64 });
  },
};

export default employeeService;
